import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import { TelemetryPoint, Finding, Severity, GEA_DEFECT_REGISTRY } from '../types';
import { calculateAssetGeometry, E3_HARDWARE_H_FOV, E3_HARDWARE_V_FOV, E3_EFFECTIVE_FOV_MULTIPLIER, calculateSurfaceFootprint, E3_CAMERA_WALL_OFFSET } from '../services/telemetryService';

const getDisplayTitle = (type: string) => {
    const match = GEA_DEFECT_REGISTRY.find(d => d.type === type || d.title === type);
    return match ? match.title : type;
};

interface TrajectoryMapAProps {
    points: TelemetryPoint[];
    findings?: Finding[];
    onClose: () => void;
    range?: [number, number];
    modelPoints?: TelemetryPoint[];
    previewMode?: 'MODEL' | 'PATH' | 'BOTH';
    mapMode?: 'ANALYSIS' | 'PLANNING';
    minimal?: boolean;
    currentTime?: number;
    baseTimestamp?: number;
    targetOverlap?: number;
    forceShowCaptureLocations?: boolean;
    showCapOnly?: boolean;
    hideFrustrums?: boolean;
    overrideGeometry?: {
        radius: number;
        baseZ: number;
        coneHeight: number;
        cylinderHeight: number;
        center: { x: number, y: number };
        coneBottomRadius?: number;
    };
    initialCloudOffset?: { x: number, y: number };
    onAlignChange?: (offset: { x: number, y: number }) => void;
}

const TAN_H = Math.tan(((E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));
const TAN_V = Math.tan(((E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));

const applyQuaternion = (p: { x: number, y: number, z: number }, q: { x: number, y: number, z: number, w: number }) => {
    const { x, y, z } = p, { x: qx, y: qy, z: qz, w: qw } = q;
    const tx = 2 * (qy * z - qz * y), ty = 2 * (qz * x - qx * z), tz = 2 * (qx * y - qy * x);
    return { x: x + qw * tx + (qy * tz - qz * ty), y: y + qw * ty + (qz * tx - qx * tz), z: z + qw * tz + (qx * ty - qy * tx) };
};

const severityColors: Record<Severity, string> = {
    [Severity.NONE]: '#22c55e',
    [Severity.LOW]: '#3b82f6',
    [Severity.MEDIUM]: '#eab308',
    [Severity.HIGH]: '#f97316',
    [Severity.CRITICAL]: '#ef4444',
};

// ── GEA BRAND PALETTE (Ported from pdfService) ─────────────────────────────────
const GEA_BLUE_RGB: [number, number, number] = [0, 0, 169];    // #0000a9
const GEA_SLATE_RGB: [number, number, number] = [30, 41, 59];   // slate-800
const GEA_LIGHT_RGB: [number, number, number] = [241, 245, 249]; // slate-100
const GEA_MID_RGB: [number, number, number] = [148, 163, 184]; // slate-400
const GEA_DARK_RGB: [number, number, number] = [15, 23, 42];   // slate-900
const WHITE_RGB: [number, number, number] = [255, 255, 255];

export const TrajectoryMapA: React.FC<TrajectoryMapAProps> = ({
    points, findings = [], onClose, range, modelPoints = [], previewMode = 'BOTH', mapMode = 'ANALYSIS', minimal = false, currentTime, baseTimestamp, targetOverlap, forceShowCaptureLocations, showCapOnly, hideFrustrums: initialHideFrustrums, overrideGeometry, initialCloudOffset, onAlignChange
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [viewAngle, setViewAngle] = useState({ pitch: 90, yaw: 0 });
    const [zoom, setZoom] = useState(1.0);
    const [focusCoord, setFocusCoord] = useState<{ x: number, y: number, z: number } | null>(null);
    const [interactionMode, setInteractionMode] = useState<'ORBIT' | 'PAN'>('ORBIT');
    const [pointDensity, setPointDensity] = useState(50);

    const [hideFrustrums, setHideFrustrums] = useState(true);
    const [hideTrajectory, setHideTrajectory] = useState(true);

    const [isAligning, setIsAligning] = useState(false);
    const [cloudOffset, setCloudOffset] = useState(initialCloudOffset || { x: 0, y: 0 });
    const [selectedDefectIdx, setSelectedDefectIdx] = useState<number | null>(null);
    const [popupOffsetManual, setPopupOffsetManual] = useState({ x: 100, y: -100 });
    const [popupScaleFactor, setPopupScaleFactor] = useState(1.0);
    const [hasInitializedView, setHasInitializedView] = useState(false);
    const popupRef = useRef<HTMLDivElement>(null);
    const isDraggingPopupRef = useRef(false);

    const velocityRef = useRef({ pitch: 0, yaw: 0 });
    const isDraggingRef = useRef(false);
    const lastMousePosRef = useRef({ x: 0, y: 0 });
    const requestRef = useRef<number | null>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // verifiedFindings definition moved down after getOpticalDistanceToCloud


    const logStart = useMemo(() => points.length > 0 ? points[0].timestamp : 0, [points]);
    const displayTime = currentTime !== undefined ? currentTime : 0;
    const floorZ = useMemo(() => points.length > 0 ? points[0].z : 0, [points]);

    const assetGeometry = useMemo(() => {
        if (overrideGeometry) return { centerX: overrideGeometry.center.x, centerY: overrideGeometry.center.y, radius: overrideGeometry.radius, diameter: overrideGeometry.radius * 2, minZ: overrideGeometry.baseZ, maxZ: overrideGeometry.baseZ + overrideGeometry.coneHeight + overrideGeometry.cylinderHeight, totalHeight: overrideGeometry.coneHeight + overrideGeometry.cylinderHeight, validPoints: modelPoints || [], coneBottomRadius: overrideGeometry.coneBottomRadius ?? 0.5, coneHeight: overrideGeometry.coneHeight, cylinderHeight: overrideGeometry.cylinderHeight, groundZ: overrideGeometry.baseZ };
        const geom = calculateAssetGeometry(modelPoints && modelPoints.length > 50 ? modelPoints : points);
        return { ...geom, centerX: geom.center.x, centerY: geom.center.y, diameter: geom.radius * 2, groundZ: floorZ };
    }, [points, overrideGeometry, floorZ, modelPoints]);

    const getLocalWallRadius = useCallback((z: number) => {
        const { minZ, radius, coneHeight, coneBottomRadius } = assetGeometry, relZ = z - minZ;
        if (coneHeight && relZ < coneHeight) return (coneBottomRadius || 0.5) + (radius - (coneBottomRadius || 0.5)) * Math.max(0, relZ / coneHeight);
        return radius;
    }, [assetGeometry]);

    const getOpticalDistanceToCloud = useCallback((pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }, defaultRadius: number) => {
        if (!modelPoints || modelPoints.length === 0) {
            const dx = pos.x - (assetGeometry.centerX), dy = pos.y - (assetGeometry.centerY);
            return Math.abs(defaultRadius - Math.sqrt(dx * dx + dy * dy));
        }

        const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
        let minDist = 5.0;
        let found = false;

        const step = Math.max(1, Math.ceil(modelPoints.length / 1000));
        for (let i = 0; i < modelPoints.length; i += step) {
            const p = modelPoints[i];
            const dx = (p.x + cloudOffset.x) - pos.x, dy = (p.y + cloudOffset.y) - pos.y, dz = p.z - pos.z;
            const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
            if (d > 5.0 || d < 0.1) continue;

            const dot = (dx * vLook.x + dy * vLook.y + dz * vLook.z) / d;
            if (dot > 0.95) {
                if (!found || d < minDist) {
                    minDist = d;
                    found = true;
                }
            }
        }

        return found ? minDist : 1.5;
    }, [modelPoints, assetGeometry]);

    const captureIndices = useMemo(() => {
        if (!points || points.length === 0) return [];
        let lastIdx = 0, indices: number[] = [0];
        const strideScale = Math.max(0.01, 1 - ((targetOverlap ?? 25) / 100));

        for (let i = 1; i < points.length; i++) {
            const p = points[i];
            const pPrev = points[lastIdx];
            if (p.forceCapture) { indices.push(i); lastIdx = i; continue; }

            const standoff = getOpticalDistanceToCloud({ x: p.x, y: p.y, z: p.z }, { x: p.qx, y: p.qy, z: p.qz, w: p.qw }, assetGeometry.radius);
            const frameW = 2 * Math.max(0.1, standoff - E3_CAMERA_WALL_OFFSET) * TAN_H;
            const frameH = 2 * Math.max(0.1, standoff - E3_CAMERA_WALL_OFFSET) * TAN_V;

            const dx = p.x - pPrev.x, dy = p.y - pPrev.y, dz = p.z - pPrev.z, totalDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
            const dominantStride = Math.abs(dz) > Math.sqrt(dx * dx + dy * dy) ? frameH : frameW;

            if (totalDist >= dominantStride * strideScale) { indices.push(i); lastIdx = i; }
        }
        return indices;
    }, [points, assetGeometry, targetOverlap, getOpticalDistanceToCloud]);

    const activeCaptureIndex = useMemo(() => {
        if (displayTime === undefined || captureIndices.length === 0) return -1;
        let bestIdx = 0; const tBase = baseTimestamp !== undefined ? baseTimestamp : logStart;
        for (let i = 0; i < captureIndices.length; i++) { if (points[captureIndices[i]].timestamp - tBase <= displayTime + 0.05) bestIdx = i; else break; }
        return bestIdx;
    }, [displayTime, captureIndices, points, logStart, baseTimestamp]);

    const activeFootprint = useMemo(() => {
        if (activeCaptureIndex === -1 || points.length === 0) return null;
        const p = points[captureIndices[activeCaptureIndex]];
        const rot = { x: p.qx, y: p.qy, z: p.qz, w: p.qw };

        const realStandoff = getOpticalDistanceToCloud({ x: p.x, y: p.y, z: p.z }, rot, assetGeometry.radius);
        const opticalStandoff = Math.max(0.01, realStandoff - E3_CAMERA_WALL_OFFSET);

        return {
            standoff: opticalStandoff,
            ...calculateSurfaceFootprint(opticalStandoff, assetGeometry.radius),
            standoffCm: Math.round(opticalStandoff * 100),
            centerStandoffCm: Math.round(realStandoff * 100)
        };
    }, [activeCaptureIndex, captureIndices, points, assetGeometry, getOpticalDistanceToCloud]);

    const verifiedFindings = useMemo(() => {
        return findings
            .filter(f => f.isVerified)
            .map(f => {
                if (!f.timestamp || points.length === 0) return f;

                let p1 = points[0], p2 = points[0], lerp = 0;
                const t = f.timestamp;

                if (points.length > 1) {
                    let l = 0, h = points.length - 1;
                    if (t >= points[h].timestamp) { p1 = points[h]; p2 = points[h]; }
                    else if (t <= points[0].timestamp) { p1 = points[0]; p2 = points[0]; }
                    else {
                        while (l <= h) { const m = (l + h) >>> 1; if (points[m].timestamp <= t) l = m + 1; else h = m - 1; }
                        const idx = Math.max(0, l - 1); p1 = points[idx]; p2 = points[idx + 1] || points[idx];
                        const span = p2.timestamp - p1.timestamp; if (span > 0.001) lerp = (t - p1.timestamp) / span;
                    }
                }

                const pos = { x: p1.x + (p2.x - p1.x) * lerp, y: p1.y + (p2.y - p1.y) * lerp, z: p1.z + (p2.z - p1.z) * lerp };
                const quat = { qx: p1.qx + (p2.qx - p1.qx) * lerp, qy: p1.qy + (p2.qy - p1.qy) * lerp, qz: p1.qz + (p2.qz - p1.qz) * lerp, qw: p1.qw + (p2.qw - p1.qw) * lerp };
                const mag = Math.sqrt(quat.qx ** 2 + quat.qy ** 2 + quat.qz ** 2 + quat.qw ** 2) || 1;
                const rot = { x: quat.qx / mag, y: quat.qy / mag, z: quat.qz / mag, w: quat.qw / mag };

                // Project along look-vector to hit the wall
                const look = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
                const wallDist = getOpticalDistanceToCloud(pos, rot, assetGeometry.radius);

                const projectedPos = {
                    x: pos.x + look.x * wallDist,
                    y: pos.y + look.y * wallDist,
                    z: pos.z + look.z * wallDist
                };

                return { ...f, spatialCoords: projectedPos };
            });
    }, [findings, points, assetGeometry, getOpticalDistanceToCloud]);

    const viewFocus = useMemo(() => ({ x: assetGeometry.centerX, y: assetGeometry.centerY, z: (assetGeometry.minZ + assetGeometry.maxZ) / 2 }), [assetGeometry]);
    const zeroHeading = useMemo(() => points.length > 0 ? Math.atan2(points[0].y - assetGeometry.centerY, points[0].x - assetGeometry.centerX) : 0, [points, assetGeometry]);

    const setView = useCallback((type: 'Top' | 'Front' | 'Side') => {
        setFocusCoord(null);
        const zeroDeg = (zeroHeading * 180 / Math.PI);
        if (type === 'Top') { setViewAngle({ pitch: 180, yaw: 90 - zeroDeg }); setZoom(1.4); }
        else if (type === 'Front') { setViewAngle({ pitch: 90, yaw: 90 - zeroDeg }); setZoom(1.6); }
        else if (type === 'Side') { setViewAngle({ pitch: 90, yaw: -zeroDeg }); setZoom(1.6); }
    }, [zeroHeading]);

    useEffect(() => {
        if (!hasInitializedView && points.length > 2) {
            const timer = setTimeout(() => {
                setView('Side');
                setHasInitializedView(true);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [points, setView, hasInitializedView]);

    const drawMap = useCallback((
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        angle: { pitch: number, yaw: number },
        zoomVal: number,
        focus: { x: number, y: number, z: number },
        options: {
            hideFrustrums?: boolean,
            highlightDefectIdx?: number | null,
            showPath?: boolean,
            showModel?: boolean,
            minimal?: boolean,
            showGuides?: boolean
        } = {}
    ) => {
        const { hideFrustrums = false, highlightDefectIdx = null, showPath = true, showModel = true, minimal: isMin = false, showGuides = false } = options;

        ctx.fillStyle = '#020617'; ctx.fillRect(0, 0, width, height);
        const baseScale = Math.min(width, height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoomVal;
        const rY = (angle.yaw * Math.PI) / 180, rP = (angle.pitch * Math.PI) / 180, cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP), midX = width / 2, midY = height / 2;

        const project = (px: number, py: number, pz: number) => {
            const dx = px - focus.x, dy = py - focus.y, dz = pz - focus.z;
            const nx = -dx * baseScale, ny = -dy * baseScale, nz = dz * baseScale;
            const rx = nx * cosY - ny * sinY, ry1 = nx * sinY + ny * cosY, ry = ry1 * cosP - nz * sinP, rz = ry1 * sinP + nz * cosP;
            return { x: midX + rx, y: midY + ry, rz };
        };

        const drawFrustum = (pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }, color: string, isSolid: boolean = false, thick: number = 1) => {
            const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
            const opticalL = Math.min(2.5, getOpticalDistanceToCloud(pos, rot, assetGeometry.radius));
            const hw = opticalL * TAN_H, hh = opticalL * TAN_V, cameraOffset = applyQuaternion({ x: E3_CAMERA_WALL_OFFSET, y: 0, z: 0 }, rot);
            const camX = pos.x + cameraOffset.x, camY = pos.y + cameraOffset.y, camZ = pos.z + cameraOffset.z;
            const verts = [{ x: 0, y: 0, z: 0 }, { x: opticalL, y: -hw, z: -hh }, { x: opticalL, y: hw, z: -hh }, { x: opticalL, y: hw, z: hh }, { x: opticalL, y: -hw, z: hh }].map(v => { const r = applyQuaternion(v, rot); return project(camX + r.x, camY + r.y, camZ + r.z); });

            ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)'; ctx.lineWidth = 0.5; ctx.beginPath(); for (let i = 1; i <= 4; i++) { ctx.moveTo(verts[0].x, verts[0].y); ctx.lineTo(verts[i].x, verts[i].y); } ctx.stroke();
            ctx.strokeStyle = color; ctx.lineWidth = isSolid ? thick * 1.5 : thick; ctx.beginPath(); ctx.moveTo(verts[1].x, verts[1].y); ctx.lineTo(verts[2].x, verts[2].y); ctx.lineTo(verts[3].x, verts[3].y); ctx.lineTo(verts[4].x, verts[4].y); ctx.lineTo(verts[1].x, verts[1].y); ctx.stroke();
            if (isSolid) { ctx.globalAlpha = 0.2; ctx.fillStyle = color; ctx.fill(); ctx.globalAlpha = 1.0; }
        };

        // Ground Plane
        const { centerX, centerY, radius } = assetGeometry;
        const ringX = centerX;
        const ringY = centerY;
        const groundSize = radius * 4.5;
        const corners = [{ x: ringX - groundSize, y: ringY - groundSize }, { x: ringX + groundSize, y: ringY - groundSize }, { x: ringX + groundSize, y: ringY + groundSize }, { x: ringX - groundSize, y: ringY + groundSize }].map(c => project(c.x, c.y, floorZ));

        ctx.beginPath(); ctx.moveTo(corners[0].x, corners[0].y); ctx.lineTo(corners[1].x, corners[1].y); ctx.lineTo(corners[2].x, corners[2].y); ctx.lineTo(corners[3].x, corners[3].y); ctx.closePath(); ctx.fillStyle = 'rgba(6, 182, 212, 0.05)'; ctx.fill(); ctx.strokeStyle = 'rgba(6, 182, 212, 0.2)'; ctx.lineWidth = 1; ctx.stroke();

        // 3D Axis
        const drawAxis = (endPt: { x: number, y: number, z: number }, color: string, labelPos: string) => {
            const p0 = project(0, 0, 0), p1 = project(endPt.x, endPt.y, endPt.z);
            ctx.beginPath(); ctx.moveTo(p0.x, p0.y); ctx.lineTo(p1.x, p1.y); ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
            if (labelPos) { ctx.font = '8px JetBrains Mono'; ctx.textAlign = 'center'; ctx.fillStyle = color; ctx.fillText(labelPos, p1.x, p1.y - 6); }
        };


        drawAxis({ x: 1.5, y: 0, z: 0 }, '#ef4444', '+X');
        drawAxis({ x: 0, y: -1.5, z: 0 }, '#22c55e', '+Y');
        drawAxis({ x: 0, y: 0, z: 1.5 }, '#3b82f6', '+Z');

        // Model
        if (showModel && modelPoints && modelPoints.length > 0) {
            const densityFactor = Math.max(1, Math.ceil((100 - pointDensity) / 10));
            const skip = (isDraggingRef.current || isAligning) ? Math.max(densityFactor, Math.ceil(modelPoints.length / 20000)) : densityFactor;
            ctx.beginPath(); ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
            for (let i = 0; i < modelPoints.length; i += skip) { const p = modelPoints[i], pr = project(p.x + cloudOffset.x, p.y + cloudOffset.y, p.z); ctx.rect(pr.x - 0.5, pr.y - 0.5, 1, 1); } ctx.fill();
        }

        // Findings
        if (verifiedFindings && verifiedFindings.length > 0) {
            verifiedFindings.forEach((f, idx) => {
                if (!f.spatialCoords) return;
                const pr = project(f.spatialCoords.x + cloudOffset.x, f.spatialCoords.y + cloudOffset.y, f.spatialCoords.z);
                const isSelected = highlightDefectIdx === idx;
                const registryEntry = GEA_DEFECT_REGISTRY.find(d => d.type === f.type || d.title === f.type);
                const color = registryEntry?.hex || severityColors[f.severity] || '#f97316';

                ctx.fillStyle = color; ctx.beginPath(); ctx.arc(pr.x, pr.y, isSelected ? 8 : 4, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = color; ctx.lineWidth = isSelected ? 3 : 1; ctx.beginPath(); ctx.arc(pr.x, pr.y, (isSelected ? 14 : 8) + Math.sin(Date.now() / 200) * 2, 0, Math.PI * 2); ctx.stroke();

                if (!isMin && !isSelected) { ctx.fillStyle = '#fff'; ctx.font = 'bold 9px JetBrains Mono'; ctx.fillText(getDisplayTitle(f.type).toUpperCase(), pr.x + 10, pr.y); }
            });
        }

        // Path
        if (showPath && points.length > 0) { ctx.beginPath(); ctx.lineWidth = 2.0; ctx.strokeStyle = 'rgba(6, 182, 212, 0.8)'; points.forEach((p, i) => { const pr = project(p.x + cloudOffset.x, p.y + cloudOffset.y, p.z); if (i === 0) ctx.moveTo(pr.x, pr.y); else ctx.lineTo(pr.x, pr.y); }); ctx.stroke(); }

        // Frustums
        if (!hideFrustrums) {
            captureIndices.forEach((idx, cIdx) => {
                const p = points[idx], rot = { x: p.qx, y: p.qy, z: p.qz, w: p.qw }, isAct = activeCaptureIndex === cIdx;
                if (showCapOnly && cIdx !== activeCaptureIndex && cIdx !== activeCaptureIndex - 1) return;
                if (range && (p.timestamp - (baseTimestamp || logStart) < range[0] || p.timestamp - (baseTimestamp || logStart) > range[1])) return;
                const color = isAct ? 'rgba(34, 197, 94, 0.9)' : 'rgba(250, 204, 21, 0.5)';
                drawFrustum({ x: p.x + cloudOffset.x, y: p.y + cloudOffset.y, z: p.z }, rot, color, isAct, isAct ? 2 : 1);
            });
        }

        if (showGuides) {
            // Radius Circle at Center
            const segments = 64;
            const ringRadius = assetGeometry.radius * 1.25; // Bigger ring outside asset
            ctx.beginPath();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
            for (let i = 0; i <= segments; i++) {
                const theta = (i / segments) * Math.PI * 2;
                const px = ringX + ringRadius * Math.cos(theta);
                const py = ringY + ringRadius * Math.sin(theta);
                const pr = project(px, py, focus.z);
                if (i === 0) ctx.moveTo(pr.x, pr.y); else ctx.lineTo(pr.x, pr.y);
            }
            ctx.stroke();

            // Degree Labels
            ctx.font = 'bold 9px JetBrains Mono';
            ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
            ctx.textAlign = 'center';
            for (let d = 0; d < 360; d += 45) {
                const theta = zeroHeading - (d * Math.PI / 180);
                const px = ringX + ringRadius * 1.15 * Math.cos(theta);
                const py = ringY + ringRadius * 1.15 * Math.sin(theta);
                const pr = project(px, py, focus.z);
                ctx.fillText(`${d}°`, pr.x, pr.y);
            }

            // Height Indicator (Vertical Gauge)
            if (Math.abs((angle.pitch % 180) - 90) <= 45) {
                // Position gauge so it's consistently visible on the screen-right side relative to current yaw
                // Camera facing origin from angle.yaw. Screen-right vector in world space:
                const radY = (angle.yaw * Math.PI) / 180;
                const rightX = Math.cos(radY);
                const rightY = Math.sin(radY);

                const gaugeSideOffset = assetGeometry.radius * 1.4;
                const gaugeX = ringX + rightX * gaugeSideOffset;
                const gaugeY = ringY + rightY * gaugeSideOffset;

                ctx.beginPath();
                ctx.strokeStyle = 'rgba(34, 211, 238, 0.9)'; // Brighter Cyan (cyan-400)
                ctx.lineWidth = 4;
                const pBottom = project(gaugeX, gaugeY, assetGeometry.minZ);
                const pTop = project(gaugeX, gaugeY, assetGeometry.maxZ);
                ctx.moveTo(pBottom.x, pBottom.y);
                ctx.lineTo(pTop.x, pTop.y);
                ctx.stroke();

                // Ticks every 1m
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 16px JetBrains Mono';
                ctx.textAlign = 'left';
                for (let z = Math.floor(assetGeometry.minZ); z <= Math.ceil(assetGeometry.maxZ); z++) {
                    const pTick = project(gaugeX, gaugeY, z);
                    ctx.beginPath();
                    ctx.lineWidth = 2;
                    ctx.strokeStyle = 'rgba(34, 211, 238, 0.9)';
                    ctx.moveTo(pTick.x, pTick.y);
                    ctx.lineTo(pTick.x + 10, pTick.y);
                    ctx.stroke();
                    ctx.fillText(`${z}m`, pTick.x + 15, pTick.y + 6);
                }
            }
        }

        // UAV
        if (!isMin && displayTime !== undefined && points.length > 0) {
            const tT = displayTime + (baseTimestamp !== undefined ? baseTimestamp : logStart);
            let p1 = points[0], p2 = points[0], lerp = 0;
            if (points.length > 1) {
                let l = 0, h = points.length - 1;
                if (tT >= points[h].timestamp) p1 = points[h], p2 = points[h];
                else { while (l <= h) { const m = (l + h) >>> 1; if (points[m].timestamp <= tT) l = m + 1; else h = m - 1; } const idx = Math.max(0, l - 1); p1 = points[idx]; p2 = points[idx + 1] || points[idx]; const span = p2.timestamp - p1.timestamp; if (span > 0.001) lerp = (tT - p1.timestamp) / span; }
            }
            const rawPos = { x: p1.x + (p2.x - p1.x) * lerp, y: p1.y + (p2.y - p1.y) * lerp, z: p1.z + (p2.z - p1.z) * lerp };
            const uav = project(rawPos.x + cloudOffset.x, rawPos.y + cloudOffset.y, rawPos.z);
            ctx.fillStyle = '#fff'; ctx.shadowBlur = 10; ctx.shadowColor = '#06b6d4'; ctx.beginPath(); ctx.arc(uav.x, uav.y, 5, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
        }
    }, [assetGeometry, cloudOffset, floorZ, modelPoints, verifiedFindings, points, captureIndices, activeCaptureIndex, logStart, displayTime, baseTimestamp, range, getOpticalDistanceToCloud, zeroHeading, pointDensity, isAligning]);

    const handleGenerateRepairPDF = async () => {
        const reportWindow = window.open('about:blank', '_blank');
        setIsGeneratingPdf(true);
        try {
            const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' }) as any;
            const reportId = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
            const timestamp = new Date().toLocaleString();

            const drawHeader = (doc: any, pageTitle: string) => {
                doc.setFillColor(...GEA_SLATE_RGB); doc.rect(0, 0, 210, 28, "F");
                doc.setFillColor(...GEA_BLUE_RGB); doc.rect(0, 0, 28, 28, "F");
                doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(255, 255, 255);
                doc.text("GEA", 14, 17, { align: "center" });
                doc.setFontSize(11); doc.text("REPAIR LOCATIONS  |  NDT-CHECK PRO V6.0", 35, 12);
                doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(...GEA_MID_RGB);
                doc.text(`${pageTitle.toUpperCase()}  |  REF: GEA-REPAIR-${reportId}`, 35, 19);
                doc.setDrawColor(...GEA_BLUE_RGB); doc.setLineWidth(0.5); doc.line(0, 28, 210, 28);
            };

            const drawFooter = (doc: any) => {
                doc.setFillColor(241, 245, 249); doc.rect(0, 283, 210, 14, "F");
                doc.setDrawColor(...GEA_BLUE_RGB); doc.setLineWidth(0.4); doc.line(0, 283, 210, 283);
                doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(...GEA_MID_RGB);
                doc.text("GEA Group  ·  REPAIR GUIDANCE REPORT V6.0  ·  Confidential", 15, 289);
                doc.setTextColor(...GEA_BLUE_RGB); doc.setFont("helvetica", "bold");
                doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, 195, 289, { align: "right" });
            };

            // Cover Page
            doc.setFillColor(...GEA_SLATE_RGB); doc.rect(0, 0, 210, 297, "F");
            doc.setFillColor(...GEA_BLUE_RGB); doc.rect(0, 0, 210, 60, "F");
            doc.setFillColor(255, 255, 255); doc.rect(15, 10, 40, 40, "F");
            doc.setFont("helvetica", "bold"); doc.setFontSize(28); doc.setTextColor(...GEA_BLUE_RGB);
            doc.text("GEA", 35, 36, { align: "center" });
            doc.setFontSize(22); doc.setTextColor(255, 255, 255);
            doc.text("REPAIR LOCATIONS REPORT", 105, 90, { align: "center" });
            doc.setFontSize(11); doc.setTextColor(...GEA_MID_RGB);
            doc.text(`NDT SURFACE AUDIT · BUILD V6.0`, 105, 101, { align: "center" });
            doc.setDrawColor(...GEA_BLUE_RGB); doc.setLineWidth(1); doc.line(50, 108, 160, 108);

            doc.setFillColor(...GEA_DARK_RGB); doc.roundedRect(20, 120, 170, 50, 3, 3, "F");
            doc.setFontSize(9); doc.setTextColor(...GEA_MID_RGB);
            doc.text("Date:", 30, 135); doc.text("Total Events:", 30, 143); doc.text("Ref:", 30, 151);
            doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
            doc.text(timestamp, 70, 135); doc.text(verifiedFindings.length.toString(), 70, 143); doc.text(`GEA-REPAIR-${reportId}`, 70, 151);

            // Detailed Findings
            const snapshotCanvas = document.createElement('canvas');
            snapshotCanvas.width = 600; snapshotCanvas.height = 600; // 1:1 Aspect Ratio
            const snCtx = snapshotCanvas.getContext('2d')!;

            for (let i = 0; i < verifiedFindings.length; i++) {
                const findingIndexOnPage = i % 2;
                if (findingIndexOnPage === 0) {
                    doc.addPage();
                    drawHeader(doc, `Event ${i + 1}${verifiedFindings.length > i + 1 ? ' & ' + (i + 2) : ''} Spatial Context`);
                }

                const f = verifiedFindings[i];
                const startY = 35 + (findingIndexOnPage * 125); // Offset for second finding

                // Info Section
                doc.setFillColor(241, 245, 249); doc.rect(15, startY, 180, 45, "F");
                doc.setDrawColor(...GEA_BLUE_RGB); doc.setLineWidth(0.3); doc.rect(15, startY, 180, 45);

                doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(...GEA_BLUE_RGB);
                doc.text(getDisplayTitle(f.type).toUpperCase(), 20, startY + 10);

                doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...GEA_DARK_RGB);
                const desc = doc.splitTextToSize(`Description: ${f.description || "N/A"}`, 170);
                doc.text(desc, 20, startY + 17);

                const theta = f.spatialCoords ? Math.atan2((f.spatialCoords.y + cloudOffset.y) - assetGeometry.centerY, (f.spatialCoords.x + cloudOffset.x) - assetGeometry.centerX) : 0;
                let deg = ((zeroHeading - theta) * 180 / Math.PI) % 360; if (deg < 0) deg += 360;
                // Add a "HEADING" field to display the angle relative to the start position. (Done below)

                doc.setFont("helvetica", "bold");
                doc.text(`X: ${f.spatialCoords?.x.toFixed(2)}m`, 20, startY + 37);
                doc.text(`Y: ${f.spatialCoords?.y.toFixed(2)}m`, 60, startY + 37);
                doc.text(`Z (H): ${f.spatialCoords?.z.toFixed(2)}m`, 100, startY + 37);
                doc.setTextColor(...GEA_BLUE_RGB);
                doc.text(`HEADING: ${Math.round(deg)}°`, 140, startY + 37);

                // IMAGE ROW: Original | Side | Top
                const imgY = startY + 50;
                const imgW = 58;
                const imgH = 58; // Square boxes
                const imgGap = 3;

                // 1. Original
                if (f.frameImage) {
                    doc.addImage(f.frameImage, "JPEG", 15, imgY, imgW, imgH, undefined, 'FAST');
                }
                doc.setDrawColor(...GEA_BLUE_RGB); doc.setLineWidth(0.2); doc.rect(15, imgY, imgW, imgH);
                doc.setFontSize(6); doc.setTextColor(...GEA_MID_RGB); doc.text("SURFACE IMAGE", 15, imgY - 1);

                // Derived aligned position for centering snapshots
                const alignedF = f.spatialCoords ? {
                    x: f.spatialCoords.x + cloudOffset.x,
                    y: f.spatialCoords.y + cloudOffset.y,
                    z: f.spatialCoords.z
                } : null;

                // 2. Side View
                if (alignedF) {
                    const sideYaw = -((theta * 180 / Math.PI)) - 90;
                    drawMap(snCtx, 600, 600, { pitch: 90, yaw: sideYaw }, 1.4, alignedF, {
                        hideFrustrums: true,
                        highlightDefectIdx: i,
                        showPath: !hideTrajectory,
                        showModel: true,
                        minimal: true,
                        showGuides: true
                    });
                    doc.addImage(snapshotCanvas.toDataURL("image/jpeg", 0.7), "JPEG", 15 + imgW + imgGap, imgY, imgW, imgH, undefined, 'FAST');
                    doc.rect(15 + imgW + imgGap, imgY, imgW, imgH);
                    doc.text("SIDE SPATIAL CONTEXT (3D)", 15 + imgW + imgGap, imgY - 1);
                }

                // 3. Top View
                if (alignedF) {
                    drawMap(snCtx, 600, 600, { pitch: 180, yaw: 90 - (zeroHeading * 180 / Math.PI) }, 1.5, alignedF, {
                        hideFrustrums: true,
                        highlightDefectIdx: i,
                        showPath: !hideTrajectory,
                        showModel: true,
                        minimal: true,
                        showGuides: true
                    });
                    doc.addImage(snapshotCanvas.toDataURL("image/jpeg", 0.7), "JPEG", 15 + (imgW + imgGap) * 2, imgY, imgW, imgH, undefined, 'FAST');
                    doc.rect(15 + (imgW + imgGap) * 2, imgY, imgW, imgH);
                    doc.text("TOP SPATIAL CONTEXT (3D)", 15 + (imgW + imgGap) * 2, imgY - 1);
                }

                if (findingIndexOnPage === 1 || i === verifiedFindings.length - 1) {
                    drawFooter(doc);
                }
            }

            const pdfBlob = doc.output('blob');
            const blobUrl = URL.createObjectURL(pdfBlob);

            if (reportWindow) {
                reportWindow.location.href = blobUrl;
            } else {
                const link = document.createElement('a');
                link.href = blobUrl;
                link.target = '_blank';
                link.click();
            }

            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            console.error(err);
            if (reportWindow) reportWindow.close();
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const render = useCallback(() => {
        const canvas = canvasRef.current; if (!canvas) return;
        const ctx = canvas.getContext('2d'); if (!ctx) return;
        const dpr = window.devicePixelRatio || 1, rect = canvas.getBoundingClientRect();
        if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) { canvas.width = Math.round(rect.width * dpr); canvas.height = Math.round(rect.height * dpr); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(dpr, dpr); }

        if (!isDraggingRef.current && interactionMode === 'ORBIT' && (Math.abs(velocityRef.current.pitch) > 0.01 || Math.abs(velocityRef.current.yaw) > 0.01)) {
            setViewAngle(p => ({ pitch: p.pitch + velocityRef.current.pitch, yaw: p.yaw + velocityRef.current.yaw }));
            velocityRef.current.pitch *= 0.94; velocityRef.current.yaw *= 0.94;
        }

        drawMap(ctx, rect.width, rect.height, viewAngle, zoom, focusCoord || viewFocus, {
            hideFrustrums,
            highlightDefectIdx: selectedDefectIdx,
            showPath: !hideTrajectory,
            showModel: true,
            minimal,
            showGuides: !minimal
        });

        // Overlay specific logic that depends on canvas coords (like popup)
        if (selectedDefectIdx !== null && verifiedFindings[selectedDefectIdx] && popupRef.current) {
            const f = verifiedFindings[selectedDefectIdx];
            if (f.spatialCoords) {
                const baseScale = Math.min(rect.width, rect.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
                const centerPoint = focusCoord || viewFocus, rY = (viewAngle.yaw * Math.PI) / 180, rP = (viewAngle.pitch * Math.PI) / 180, cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP), midX = rect.width / 2, midY = rect.height / 2;

                const dx = f.spatialCoords.x - centerPoint.x, dy = f.spatialCoords.y - centerPoint.y, dz = f.spatialCoords.z - centerPoint.z, nx = dx * baseScale, ny = dy * baseScale, nz = dz * baseScale, rx = nx * cosY - ny * sinY, ry1 = nx * sinY + ny * cosY, ry = ry1 * cosP - nz * sinP;
                const pr = { x: midX + rx, y: midY + ry };

                popupRef.current.style.transform = `translate(${pr.x + popupOffsetManual.x}px, ${pr.y + popupOffsetManual.y}px) scale(${popupScaleFactor})`;
                popupRef.current.style.display = 'block';

                ctx.beginPath(); ctx.moveTo(pr.x, pr.y); ctx.lineTo(pr.x + popupOffsetManual.x, pr.y + popupOffsetManual.y);
                ctx.setLineDash([4, 4]); ctx.strokeStyle = GEA_DEFECT_REGISTRY.find(d => d.type === f.type)?.hex || severityColors[f.severity] || '#f97316';
                ctx.lineWidth = 1.5; ctx.stroke(); ctx.setLineDash([]);
            }
        } else if (popupRef.current) {
            popupRef.current.style.display = 'none';
        }
    }, [drawMap, viewAngle, zoom, focusCoord, viewFocus, hideFrustrums, selectedDefectIdx, minimal, popupOffsetManual, popupScaleFactor, verifiedFindings, assetGeometry, pointDensity]);

    useEffect(() => { requestRef.current = requestAnimationFrame(function frame() { render(); requestRef.current = requestAnimationFrame(frame); }); return () => { if (requestRef.current) cancelAnimationFrame(requestRef.current); }; }, [render]);

    return (
        <div className="flex flex-col h-full bg-[#020617] animate-fadeIn overflow-hidden">
            {/* Banner */}
            <header className="px-8 py-4 bg-[#011627] border-b border-white/5 flex items-center justify-between shrink-0 text-left relative">
                <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse"></div>
                    <h1 className="text-sm font-black text-white uppercase italic tracking-tighter">Technical Audit Analysis Result</h1>
                </div>
                <button onClick={onClose} className="text-white/40 hover:text-white transition-all transform hover:scale-110 active:scale-95" title="Close Panel">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <div className="absolute bottom-4 right-16 text-[8px] font-mono text-slate-800 uppercase tracking-widest bg-black/40 px-2 py-1 pointer-events-none">V6.0 NEURAL_MAP_T_CORE</div>
            </header>

            {/* Map Area */}
            <div className="relative flex-1 w-full overflow-hidden shadow-inner">
                <canvas
                    ref={canvasRef}
                    onContextMenu={(e) => e.preventDefault()}
                    onMouseDown={(e) => {
                        isDraggingRef.current = true;
                        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
                        (e.currentTarget as any)._mouseDownPos = { x: e.clientX, y: e.clientY };
                        velocityRef.current = { pitch: 0, yaw: 0 };
                        (e.currentTarget as any)._dragButton = e.button;
                    }}
                    onMouseMove={(e) => {
                        if (!isDraggingRef.current) return;
                        const dx = e.clientX - lastMousePosRef.current.x;
                        const dy = e.clientY - lastMousePosRef.current.y;

                        if (isAligning) {
                            const cvs = canvasRef.current;
                            if (cvs) {
                                const rect = cvs.getBoundingClientRect();
                                const baseScale = Math.min(rect.width, rect.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
                                setCloudOffset(p => ({ x: p.x + dx / baseScale, y: p.y - dy / baseScale }));
                            }
                        } else if (interactionMode === 'PAN') {
                            const cvs = canvasRef.current;
                            if (cvs) {
                                const rect = cvs.getBoundingClientRect();
                                const baseScale = Math.min(rect.width, rect.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
                                const rY = (viewAngle.yaw * Math.PI) / 180, rP = (viewAngle.pitch * Math.PI) / 180;
                                const cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.cos(rP);
                                const dCx = (dx * -cosY + dy * -sinY * cosP) / baseScale;
                                const dCy = (dx * sinY + dy * -cosY * cosP) / baseScale;
                                const dCz = (dy * sinP) / baseScale;
                                setFocusCoord(p => { const base = p || viewFocus; return { x: base.x - dCx, y: base.y - dCy, z: base.z + dCz }; });
                            }
                        } else {
                            // LEFT CLICK -> ORBIT
                            velocityRef.current = { yaw: dx * 0.4, pitch: dy * 0.4 };
                            setViewAngle(p => ({ yaw: p.yaw + dx * 0.4, pitch: p.pitch + dy * 0.4 }));
                        }
                        lastMousePosRef.current = { x: e.clientX, y: e.clientY };
                    }}
                    onMouseUp={(e) => {
                        isDraggingRef.current = false;
                        const startPos = (e.currentTarget as any)._mouseDownPos;
                        if (startPos) {
                            const dist = Math.sqrt(Math.pow(e.clientX - startPos.x, 2) + Math.pow(e.clientY - startPos.y, 2));
                            if (dist < 5) { // It's a click
                                const rect = e.currentTarget.getBoundingClientRect();
                                const mouseX = e.clientX - rect.left;
                                const mouseY = e.clientY - rect.top;

                                // Hit test findings
                                const centerPoint = focusCoord || viewFocus;
                                const rY = (viewAngle.yaw * Math.PI) / 180;
                                const rP = (viewAngle.pitch * Math.PI) / 180;
                                const cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP);
                                const midX = rect.width / 2, midY = rect.height / 2;
                                const baseScale = Math.min(rect.width, rect.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;

                                let closestIdx = -1;
                                let minDist = 30; // 30px threshold

                                verifiedFindings.forEach((f, idx) => {
                                    if (!f.spatialCoords) return;
                                    const dx = (f.spatialCoords.x + cloudOffset.x) - centerPoint.x, dy = (f.spatialCoords.y + cloudOffset.y) - centerPoint.y, dz = f.spatialCoords.z - centerPoint.z;
                                    const nx = dx * baseScale, ny = dy * baseScale, nz = dz * baseScale;
                                    const rx = nx * cosY - ny * sinY, ry1 = nx * sinY + ny * cosY, ry = ry1 * cosP - nz * sinP;
                                    const sx = midX + rx, sy = midY + ry;

                                    const d = Math.sqrt(Math.pow(mouseX - sx, 2) + Math.pow(mouseY - sy, 2));
                                    if (d < minDist) {
                                        minDist = d;
                                        closestIdx = idx;
                                    }
                                });

                                if (closestIdx !== -1) {
                                    setSelectedDefectIdx(closestIdx);
                                    if (verifiedFindings[closestIdx].spatialCoords) {
                                        setFocusCoord({ ...verifiedFindings[closestIdx].spatialCoords! });
                                    }
                                }
                            }
                        }
                    }}
                    onWheel={(e) => setZoom(p => Math.min(Math.max(p * (e.deltaY > 0 ? 0.92 : 1.08), 0.1), 10))}
                    className={`w-full h-full ${isAligning ? 'cursor-move' : 'cursor-grab'}`}
                />
                <div className="absolute top-4 left-4 flex flex-col gap-2 z-50">
                    <div className="flex gap-1 bg-slate-300/80 backdrop-blur-md p-1 rounded-none border border-slate-400 shadow-lg">
                        <button onClick={() => setView('Side')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-200 text-slate-800 hover:bg-[#0000a9]/10 hover:text-slate-900 transition uppercase tracking-tighter">Side</button>
                        <button onClick={() => setView('Front')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-200 text-slate-800 hover:bg-[#0000a9]/10 hover:text-slate-900 transition uppercase tracking-tighter">Front</button>
                        <button onClick={() => setView('Top')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-200 text-slate-800 hover:bg-[#0000a9]/10 hover:text-slate-900 transition uppercase tracking-tighter">Top</button>
                        <div className="flex items-center gap-2 px-2 border-l border-slate-300">
                          <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest whitespace-nowrap">PTS</span>
                          <input
                            type="range" min={1} max={100} value={pointDensity}
                            onChange={e => setPointDensity(Number(e.target.value))}
                            className="w-20 h-1 accent-[#0000a9] cursor-pointer"
                          />
                          <span className="text-[9px] font-mono text-slate-600 w-6 text-right">{pointDensity}</span>
                        </div>
                    </div>

                    <div className="flex gap-1 bg-slate-300/80 backdrop-blur-md p-1 rounded-none border border-slate-400 shadow-lg">
                        <button
                            onClick={() => {
                                if (isAligning) {
                                    onAlignChange?.(cloudOffset); // Save to App state
                                } else {
                                    setView('Top');
                                }
                                setIsAligning(!isAligning);
                            }}
                            className={`px-3 py-1.5 rounded-none text-[9px] font-black transition uppercase tracking-tighter ${isAligning ? 'bg-amber-500 text-slate-950' : 'bg-slate-300 text-[#0000a9]'}`}
                        >
                            {isAligning ? 'Accept Alignment' : '3D PointCloud Align'}
                        </button>
                        <button
                            onClick={() => setHideFrustrums(!hideFrustrums)}
                            className={`px-3 py-1.5 rounded-none text-[9px] font-bold transition uppercase tracking-tighter ${hideFrustrums ? 'bg-slate-300 text-slate-500' : 'bg-emerald-600 text-white'}`}
                        >
                            {hideFrustrums ? 'Unhide Frustrums' : 'Hide Frustrums'}
                        </button>
                        <button
                            onClick={() => setHideTrajectory(!hideTrajectory)}
                            className={`px-3 py-1.5 rounded-none text-[9px] font-bold transition uppercase tracking-tighter ${hideTrajectory ? 'bg-slate-300 text-slate-500' : 'bg-cyan-600 text-white'}`}
                        >
                            {hideTrajectory ? 'Unhide Path' : 'Hide Path'}
                        </button>
                    </div>
                </div>

                <div className="absolute top-28 left-4 flex flex-col gap-3 z-50 pointer-events-none items-start">
                    {!minimal && activeFootprint && (
                        <>
                            <div className="bg-slate-300/80 backdrop-blur-md px-5 py-4 rounded-none border border-slate-400 text-left shadow-lg min-w-[160px] group pointer-events-auto transition-all hover:bg-slate-300"><span className="text-[9px] text-slate-500 font-black uppercase block mb-1">Standoff (D)</span><span className="text-base font-mono font-black text-slate-900">{activeFootprint.centerStandoffCm} cm</span></div>
                            <div className="bg-slate-300/80 backdrop-blur-md px-5 py-4 rounded-none border border-slate-400 text-left shadow-lg min-w-[160px] group pointer-events-auto transition-all hover:bg-slate-300"><span className="text-[9px] text-slate-800 font-black uppercase block mb-1">Surface View</span><span className="text-base font-mono font-black text-slate-700">{activeFootprint.widthCm}x{activeFootprint.heightCm} cm</span></div>
                            <button onClick={handleGenerateRepairPDF} disabled={isGeneratingPdf} className="mt-2 pointer-events-auto w-full px-5 py-3 rounded-none font-black uppercase tracking-widest text-[10px] shadow-lg transition bg-[#0000a9] hover:bg-[#0000a9]/90 text-white border border-[#0000a9]/50 flex flex-col items-center justify-center gap-1">
                                {isGeneratingPdf ? 'Generating...' : 'Repair Locations'}
                                <span className="text-[8px] font-mono text-blue-100 uppercase tracking-[0.2em]">{isGeneratingPdf ? 'Wait' : 'PDF Report'}</span>
                            </button>
                        </>
                    )}
                </div>

                {!minimal && verifiedFindings.length > 0 && (
                    <div className="absolute top-6 right-6 w-80 h-[calc(100%-48px)] z-50 flex flex-col pointer-events-none">
                        <div className="bg-slate-300/90 backdrop-blur-lg border border-slate-400 shadow-2xl flex flex-col h-full pointer-events-auto rounded-none">
                            <div className="p-5 border-b border-slate-400 flex justify-between items-center bg-slate-300">
                                <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-widest italic">V6.0 Laboratory Workbench</h4>
                                <span className="text-[9px] bg-[#0000a9] text-white px-2 py-1 font-bold rounded-none uppercase">{verifiedFindings.length} Events</span>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                {verifiedFindings.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-center opacity-30 italic">
                                        <p className="text-[10px] text-slate-500 font-black mt-2 uppercase tracking-widest">DEFINING GROUND TRUTH EV_V6.0_{selectedDefectIdx !== null ? selectedDefectIdx + 1 : 'N/A'}</p>
                                    </div>
                                ) : (
                                    verifiedFindings.map((finding, idx) => {
                                        let angleDeg: number | null = null;
                                        if (finding.spatialCoords) {
                                            const theta = Math.atan2(finding.spatialCoords.y - (assetGeometry.centerY + cloudOffset.y), finding.spatialCoords.x - (assetGeometry.centerX + cloudOffset.x));
                                            let deg = ((zeroHeading - theta) * 180 / Math.PI) % 360;
                                            if (deg < 0) deg += 360;
                                            angleDeg = Math.round(deg);
                                        }

                                        return (
                                            <div
                                                key={idx}
                                                onClick={() => {
                                                    setSelectedDefectIdx(idx);
                                                    if (finding.spatialCoords) {
                                                        setFocusCoord({ ...finding.spatialCoords });
                                                    }
                                                }}
                                                className={`flex flex-col text-left p-4 border transition group cursor-pointer rounded-none relative overflow-hidden ${selectedDefectIdx === idx ? 'border-[#0000a9] bg-slate-300 shadow-lg' : 'border-slate-400 bg-slate-300/50 hover:bg-slate-300 hover:border-[#0000a9]/30'}`}
                                            >
                                                {selectedDefectIdx === idx && <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000a9]"></div>}
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex gap-2 items-center">
                                                        <div className="w-2 h-2 rounded-none" style={{ backgroundColor: severityColors[finding.severity] }}></div>
                                                        <span className={`text-[10px] font-black uppercase tracking-widest ${selectedDefectIdx === idx ? 'text-[#0000a9]' : 'text-slate-900'}`}>{getDisplayTitle(finding.type)}</span>
                                                    </div>
                                                    <span className="text-[9px] font-black uppercase px-2 py-1 bg-slate-300 border border-slate-400" style={{ color: severityColors[finding.severity] }}>{finding.severity}</span>
                                                </div>
                                                <p className={`text-[10px] line-clamp-2 leading-relaxed font-medium ${selectedDefectIdx === idx ? 'text-slate-700' : 'text-slate-800'}`}>{finding.description}</p>
                                                <div className="mt-3 text-[9px] font-mono font-bold flex flex-wrap gap-x-3 gap-y-1 text-slate-800">
                                                    <span>X: <span className={selectedDefectIdx === idx ? 'text-[#0000a9]' : 'text-slate-700'}>{finding.spatialCoords?.x.toFixed(2)}</span></span>
                                                    <span>Y: <span className={selectedDefectIdx === idx ? 'text-[#0000a9]' : 'text-slate-700'}>{finding.spatialCoords ? finding.spatialCoords.y.toFixed(2) : '0.00'}</span></span>
                                                    <span>Z: <span className={selectedDefectIdx === idx ? 'text-[#0000a9]' : 'text-slate-700'}>{finding.spatialCoords?.z.toFixed(2)}</span></span>
                                                    {angleDeg !== null && (
                                                        <span>DEG: <span className={selectedDefectIdx === idx ? 'text-[#0000a9]' : 'text-slate-700'}>{angleDeg}°</span></span>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div
                    ref={popupRef}
                    className="absolute left-0 top-0 pointer-events-auto z-50 origin-bottom-left cursor-move select-none"
                    style={{ display: 'none' }}
                    onMouseDown={(e) => {
                        e.stopPropagation();
                        isDraggingPopupRef.current = true;
                    }}
                    onMouseMove={(e) => {
                        if (!isDraggingPopupRef.current) return;
                        e.stopPropagation();
                        setPopupOffsetManual(prev => ({ x: prev.x + e.movementX, y: prev.y + e.movementY }));
                    }}
                    onMouseUp={(e) => {
                        e.stopPropagation();
                        isDraggingPopupRef.current = false;
                    }}
                    onMouseLeave={() => {
                        isDraggingPopupRef.current = false;
                    }}
                    onWheel={(e) => {
                        e.stopPropagation();
                        setPopupScaleFactor(prev => Math.min(Math.max(prev * (e.deltaY > 0 ? 0.9 : 1.1), 0.5), 3.0));
                    }}
                >
                    {selectedDefectIdx !== null && verifiedFindings[selectedDefectIdx] && (
                        <div className="ml-2 mb-2 p-2 bg-slate-300/90 backdrop-blur-lg border shadow-2xl overflow-hidden flex flex-col gap-2 w-60 rounded-none shadow-[#0000a9]/20" style={{ borderColor: severityColors[verifiedFindings[selectedDefectIdx].severity] }}>
                            {verifiedFindings[selectedDefectIdx].frameImage && (
                                <img src={verifiedFindings[selectedDefectIdx].frameImage!} alt="Defect" className="w-full h-auto rounded-none object-cover border border-slate-400 pointer-events-none" />
                            )}
                            <div className="px-1 pb-1">
                                <span className="text-[10px] font-black text-slate-900 uppercase tracking-widest">{getDisplayTitle(verifiedFindings[selectedDefectIdx].type)}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
